import { ExLoader, ExPagination } from '@boomi/exosphere';
import { type HTMLAttributes, type ReactNode, useState } from 'react';
import FilterSelect from './FilterSelect';
import NoSearchResults from './NoSearchResults';
import SearchInput from './SearchInput';
import tableStyles from './Table.module.css';

export interface TableColumn<T> {
    header: ReactNode;
    render: (item: T) => ReactNode;
    cellProps?: (item: T) => HTMLAttributes<HTMLTableCellElement>;
    searchValue?: (item: T) => string;
    width?: string;
    filter?: {
        placeholder: string;
        showCount?: boolean;
        value: (item: T) => string;
        options?: string[];
        order?: number;
    };
}

interface TableProps<T> {
    columns: TableColumn<T>[];
    items: T[];
    rowKey: (item: T) => string;
    rowClassName?: (item: T) => string | undefined;
    searchRowClassName?: string;
    searchPendoTag: string;
    itemLabel?: string;
    loading?: boolean;
    actions?: ReactNode;
    trailingActions?: ReactNode;
    toolbar?: ReactNode;
}

const Table = <T,>({
    columns,
    items,
    rowKey,
    rowClassName,
    searchRowClassName = '',
    searchPendoTag,
    itemLabel = 'items',
    loading,
    actions,
    trailingActions,
    toolbar,
}: TableProps<T>) => {
    const [search, setSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({});
    const [selectedPage, setSelectedPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const filterableColumns = columns.filter((c) => c.filter);
    const searchableColumns = columns.filter((c) => c.searchValue);

    // apply filters
    const filteredItems = items.filter((item) =>
        filterableColumns.every(
            ({ filter }) =>
                !selectedFilters[filter!.placeholder]?.size ||
                selectedFilters[filter!.placeholder].has(filter!.value(item)),
        ),
    );

    // apply search
    const searchedAndFilteredItems = search
        ? filteredItems.filter((item) =>
              searchableColumns.some((col) =>
                  col.searchValue?.(item).toLowerCase().includes(search.toLowerCase()),
              ),
          )
        : filteredItems;

    // apply pagination
    const visibleItems = searchedAndFilteredItems.slice(
        (selectedPage - 1) * pageSize,
        selectedPage * pageSize,
    );

    return (
        <div>
            {(searchableColumns.length ||
                actions ||
                trailingActions ||
                filterableColumns.length) && (
                <div className={`${tableStyles.searchRow} ${searchRowClassName}`}>
                    {actions}
                    {actions && <div className={tableStyles.separator}></div>}
                    {searchableColumns.length && (
                        <SearchInput
                            placeholder={`Search ${itemLabel}`}
                            value={search}
                            onSearch={(value) => {
                                setSearch(value);
                                setSelectedPage(1);
                            }}
                            data-pendo-input={searchPendoTag}
                        />
                    )}
                    {filterableColumns
                        .sort((a, b) => (a.filter!.order ?? 0) - (b.filter!.order ?? 0))
                        .map(({ filter }) => {
                            const options =
                                filter!.options ?? [...new Set(items.map(filter!.value))].sort();
                            const key = filter!.placeholder;
                            return (
                                <FilterSelect
                                    key={key}
                                    placeholder={`${filter!.placeholder}${filter!.showCount ? ` (${options.length})` : ''}`}
                                    options={options}
                                    value={selectedFilters[key] ?? new Set()}
                                    onChange={(next) => {
                                        setSelectedFilters((prev) => ({ ...prev, [key]: next }));
                                        setSelectedPage(1);
                                    }}
                                />
                            );
                        })}
                    {trailingActions && (
                        <div className={tableStyles.trailingActions}>{trailingActions}</div>
                    )}
                </div>
            )}
            {toolbar}
            <div className={tableStyles.wrapper}>
                <table className={tableStyles.table}>
                    <thead className={tableStyles.tableHead}>
                        <tr>
                            {columns.map((col, i) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: No alternative to index here
                                <th key={i} style={{ width: col.width }}>
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length}>
                                    <ExLoader label={`Loading ${itemLabel}…`} />
                                </td>
                            </tr>
                        ) : searchedAndFilteredItems.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length}>
                                    <NoSearchResults itemLabel={itemLabel} search={search} />
                                </td>
                            </tr>
                        ) : (
                            visibleItems.map((item) => (
                                <tr
                                    key={rowKey(item)}
                                    className={[tableStyles.row, rowClassName?.(item)]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    {columns.map((col, i) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: No alternative to index here
                                        <td key={i} {...col.cellProps?.(item)}>
                                            {col.render(item)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <ExPagination
                className={tableStyles.pagination}
                totalItems={searchedAndFilteredItems.length}
                selectedPage={selectedPage}
                pageSize={pageSize}
                onChange={(e) => {
                    setSelectedPage(e.detail.selectedPage);
                    setPageSize(e.detail.pageSize);
                }}
            />
        </div>
    );
};

export default Table;
